/**
 * Live Analysis Service
 * Manages live demo frame analysis with request cancellation
 */
import axios from "axios";
import { JobType } from "../types";
import * as jobsAPI from "../api/jobs";

// Re-export the type for convenience
import { AnalysisResult } from "../types";

class LiveAnalysisService {
  private cancelTokenSource: any = null;
  private isProcessing: boolean = false; // Prevent concurrent requests

  /**
   * Analyze a single frame using the jobs API
   */
  async analyzeFrame(frame: Blob, jobType: JobType): Promise<AnalysisResult> {
    // Prevent concurrent requests
    if (this.isProcessing) {
      throw new Error("Analysis already in progress");
    }

    // Cancel previous request if still pending
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("New frame analysis started");
    }

    this.cancelTokenSource = axios.CancelToken.source();
    this.isProcessing = true;

    try {
      // Use the API function from jobs.ts
      const result = await jobsAPI.analyzeFrame(
        frame,
        jobType,
        this.cancelTokenSource.token
      );

      return result;
    } catch (error: any) {
      if (axios.isCancel(error)) {
        throw new Error("Request cancelled");
      }
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Cancel any pending analysis
   */
  cancelPendingAnalysis() {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("Analysis cancelled by user");
      this.cancelTokenSource = null;
    }
    this.isProcessing = false;
  }
}

export const liveAnalysisService = new LiveAnalysisService();
